import { Button } from '@fyos/components';
import { X } from 'lucide-react';
import { Category, Tag } from '@/lib/itemApi';
import { WorkspaceMember } from '@/lib/workspaceMemberApi';

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  tags: Tag[];
  members: WorkspaceMember[];
  filterCategory: string;
  setFilterCategory: (v: string) => void;
  filterPriority: string;
  setFilterPriority: (v: string) => void;
  filterTag: string;
  setFilterTag: (v: string) => void;
  filterAssignee: string;
  setFilterAssignee: (v: string) => void;
  onClearAll: () => void;
}

export default function FilterModal({
  open,
  onClose,
  categories,
  tags,
  members,
  filterCategory,
  setFilterCategory,
  filterPriority,
  setFilterPriority,
  filterTag,
  setFilterTag,
  filterAssignee,
  setFilterAssignee,
  onClearAll,
}: FilterModalProps) {
  if (!open) return null;

  const activeCount =
    (filterCategory ? 1 : 0) +
    (filterPriority ? 1 : 0) +
    (filterTag ? 1 : 0) +
    (filterAssignee ? 1 : 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50 w-full max-w-2xl bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h2>
            {activeCount > 0 && (
              <span className="h-5 w-5 rounded-full bg-brand-500 text-[10px] text-white flex items-center justify-center">
                {activeCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={onClearAll}>
                Clear all
              </Button>
            )}
            <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Filter sections */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Category */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Category</span>
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <button
                onClick={() => setFilterCategory('')}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  !filterCategory ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setFilterCategory(filterCategory === cat.id ? '' : cat.id)}
                  className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: filterCategory === cat.id ? cat.color : cat.color + '20',
                    color: filterCategory === cat.id ? '#fff' : cat.color,
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Priority</span>
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {['low', 'medium', 'high', 'critical'].map(p => (
                <button
                  key={p}
                  onClick={() => setFilterPriority(filterPriority === p ? '' : p)}
                  className={`px-2.5 py-1 rounded text-xs font-medium capitalize transition-colors ${
                    filterPriority === p
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Tag</span>
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                <button
                  onClick={() => setFilterTag('')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    !filterTag ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  All
                </button>
                {tags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => setFilterTag(filterTag === tag.id ? '' : tag.id)}
                    className="px-2.5 py-1 rounded text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: filterTag === tag.id ? tag.color : tag.color + '20',
                      color: filterTag === tag.id ? '#fff' : tag.color,
                    }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Assignee */}
          {members.length > 0 && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Assignee</span>
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                <button
                  onClick={() => setFilterAssignee('')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    !filterAssignee ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  All
                </button>
                {members.map(m => (
                  <button
                    key={m.user_id}
                    onClick={() => setFilterAssignee(filterAssignee === m.user_id ? '' : m.user_id)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      filterAssignee === m.user_id
                        ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {m.name || m.email}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
